/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * What a pricing model SAYS, without a language: which phrase each part of
 * the description is and which numbers fill it (docs/plan-aanbodmodellen-
 * 2026-10-15.md, "Beheer"). ONE builder for the two places that word a model:
 *
 *   src/lib/offerPricingText.ts  the public site and the portal, through the
 *                                caller's `t` and src/lib/formatters.ts
 *                                (`offer_pricing.*`, both baselines)
 *   describeOfferPricingNl       below: the Dutch-only surfaces that have no
 *                                `t` — the admin's offer editor and review
 *                                card, and the two review mails the server
 *                                sends (it cannot load TypeScript)
 *
 * So a staffel is "25% tot 3.500 kWh · 22% tot 5.000 kWh · … · 14% daarboven"
 * on the profile page, in the portal, in the admin and in the mail alike — and
 * its ladder (staffelLadderBands: "tot 3.500 kWh | 25%", "3.500 – 5.000 kWh |
 * 22%", …) wherever a surface can draw rows: the
 * band boundaries, the bound variants and the order come from here; only the
 * words differ per surface. The Dutch phrases of the two volume models mirror
 * `offer_pricing.*` in src/i18n/nl.json word for word —
 * src/lib/__tests__/offerPricingDescribe.test.ts holds them to it.
 *
 * Plain ESM JavaScript, no imports: the server (tsc, NodeNext) and the client
 * share it.
 */

/** @typedef {import('../types/api/offerPricing.js').OfferPricing} OfferPricing */

/**
 * One phrase of a description: its leaf key and the numbers that fill it. A
 * number's kind decides its format — `pct` a percentage as the row prints it,
 * `kwh` a whole volume, `rate` a € per kWh at four decimals, `min`/`max` a
 * yearly bound in € with cents only when it has them.
 *
 * @typedef {{
 *   key: OfferPricingPhraseKey,
 *   values: { pct?: number, kwh?: number, rate?: number, min?: number, max?: number },
 * }} OfferPricingPhrase
 */

/**
 * @typedef {'model_commission' | 'model_subscription' | 'model_fixed_rate'
 *   | 'tier_until' | 'tier_above'
 *   | 'kwh_fee' | 'kwh_fee_min' | 'kwh_fee_max' | 'kwh_fee_min_max'
 *   | 'capped_min' | 'capped_max' | 'capped_min_max'} OfferPricingPhraseKey
 */

/** How the phrases of one description are joined (a staffel has one per band). */
export const OFFER_PRICING_PHRASE_SEPARATOR = ' · ';

/**
 * The phrases of a model, in reading order; null for no model. A staffel
 * names each band's % up to the next band's lower bound and the last one
 * "daarboven"; a vastrecht offer picks the phrase for the bounds it has
 * (both, one or none) — without a vastrecht, a commission with a minimum
 * and/or maximum (`capped_*`, at least one bound by the sanitiser's rule);
 * the three single-figure models are one phrase each.
 *
 * @param {OfferPricing | null | undefined} pricing
 * @returns {OfferPricingPhrase[] | null}
 */
export function offerPricingPhrases(pricing) {
  if (!pricing) return null;
  switch (pricing.model) {
    case 'commission':
      return [{ key: 'model_commission', values: { pct: pricing.commissionPct } }];
    case 'subscription':
      return [{ key: 'model_subscription', values: {} }];
    case 'fixed_rate':
      return [{ key: 'model_fixed_rate', values: { rate: pricing.eurPerKwh } }];
    case 'tiered': {
      const { tiers } = pricing;
      return tiers.map((tier, i) => {
        const next = tiers[i + 1];
        return next
          ? { key: 'tier_until', values: { pct: tier.commissionPct, kwh: next.fromKwh } }
          : { key: 'tier_above', values: { pct: tier.commissionPct } };
      });
    }
    case 'commission_plus_kwh_fee': {
      const { yearlyMin, yearlyMax, eurPerKwh } = pricing;
      const bounds =
        yearlyMin !== null && yearlyMax !== null
          ? 'min_max'
          : yearlyMin !== null
            ? 'min'
            : yearlyMax !== null
              ? 'max'
              : null;
      // No vastrecht and no bound is the plain commission (the sanitiser
      // refuses to store it, a hand-built object may still say it).
      if (eurPerKwh === null && bounds === null) {
        return [{ key: 'model_commission', values: { pct: pricing.commissionPct } }];
      }
      /** @type {OfferPricingPhraseKey} */
      const key =
        eurPerKwh === null
          ? /** @type {OfferPricingPhraseKey} */ (`capped_${bounds}`)
          : bounds === null
            ? 'kwh_fee'
            : /** @type {OfferPricingPhraseKey} */ (`kwh_fee_${bounds}`);
      return [
        {
          key,
          values: {
            pct: pricing.commissionPct,
            ...(eurPerKwh !== null ? { rate: eurPerKwh } : {}),
            ...(yearlyMin !== null ? { min: yearlyMin } : {}),
            ...(yearlyMax !== null ? { max: yearlyMax } : {}),
          },
        },
      ];
    }
    default:
      return null;
  }
}

/**
 * @typedef {'ladder_until' | 'ladder_between' | 'ladder_from'} StaffelLadderPhraseKey
 */

/**
 * One band of a staffel as a row of its ladder: the phrase that names its kWh
 * range, the range's bounds and the band's %.
 *
 * @typedef {{ key: StaffelLadderPhraseKey, from: number, to: number | null, pct: number }} StaffelLadderBand
 */

/**
 * A staffel as a ladder, one row per band in order (the bullet's two-column
 * form, src/components/offers/StaffelLadder.tsx): the first band "tot {to}
 * kWh", a middle one "{from} – {to} kWh", the last "vanaf {from} kWh". `to` is
 * the next band's lower bound, which belongs to the NEXT band (tierFor in
 * src/lib/offerPricing.js), so "tot 3.500 kWh" says what "25% tot 3.500 kWh"
 * always said.
 *
 * @param {readonly { fromKwh: number, commissionPct: number }[]} tiers ascending, the first from 0
 * @returns {StaffelLadderBand[]}
 */
export function staffelLadderBands(tiers) {
  return tiers.map((tier, i) => {
    const next = tiers[i + 1];
    const to = next ? next.fromKwh : null;
    /** @type {StaffelLadderPhraseKey} */
    const key =
      to === null
        ? 'ladder_from'
        : i === 0 && tier.fromKwh === 0
          ? 'ladder_until'
          : 'ladder_between';
    return { key, from: tier.fromKwh, to, pct: tier.commissionPct };
  });
}

// ——— Dutch, for the surfaces without a `t` ———————————————————————————————

/**
 * The Dutch phrases. The nine of the two volume models are `offer_pricing.*`
 * of src/i18n/nl.json verbatim (the test holds them to it); the three
 * single-figure ones are the admin's own words.
 *
 * @type {Record<OfferPricingPhraseKey, string>}
 */
const NL = {
  model_commission: '{pct}%',
  model_subscription: '0% commissie, de servicekosten zijn de prijs',
  model_fixed_rate: '{rate} per kWh',
  tier_until: '{pct}% tot {kwh} kWh',
  tier_above: '{pct}% daarboven',
  kwh_fee: '{pct}% + {rate} per kWh',
  kwh_fee_min: '{pct}% + {rate} per kWh, samen min. {min} per laadpaal per jaar',
  kwh_fee_max: '{pct}% + {rate} per kWh, samen max. {max} per laadpaal per jaar',
  kwh_fee_min_max: '{pct}% + {rate} per kWh, samen min. {min} en max. {max} per laadpaal per jaar',
  capped_min: '{pct}% commissie, min. {min} per laadpaal per jaar',
  capped_max: '{pct}% commissie, max. {max} per laadpaal per jaar',
  capped_min_max: '{pct}% commissie, min. {min} en max. {max} per laadpaal per jaar',
};

/**
 * The Dutch range phrases of a staffel's ladder — `offer_pricing.ladder_*` of
 * src/i18n/nl.json verbatim (the test holds them to it).
 *
 * @type {Record<StaffelLadderPhraseKey, string>}
 */
const NL_LADDER = {
  ladder_until: 'tot {to} kWh',
  ladder_between: '{from} – {to} kWh',
  ladder_from: 'vanaf {from} kWh',
};

/** The model's name in Dutch, for a label before a description ("Staffel: …"). */
export const OFFER_PRICING_MODEL_NAMES_NL = Object.freeze({
  commission: 'Commissie',
  subscription: 'Abonnement',
  fixed_rate: 'Vast tarief',
  tiered: 'Staffel',
  commission_plus_kwh_fee: 'Commissie + vastrecht',
});

/** @type {Map<string, Intl.NumberFormat>} */
const nlFormats = new Map();
/**
 * @param {Intl.NumberFormatOptions} opts
 * @returns {Intl.NumberFormat}
 */
function nlFormat(opts) {
  const key = JSON.stringify(opts);
  let format = nlFormats.get(key);
  if (!format) {
    format = new Intl.NumberFormat('nl-NL', opts);
    nlFormats.set(key, format);
  }
  return format;
}

/**
 * A number of a phrase in the Dutch site format — the same Intl options as
 * src/lib/formatters.ts and offerPricingText.ts use, so the two agree to the
 * byte: "17,5", "3.500", "€ 0,0057", "€ 34,99", "€ 100".
 *
 * @param {'pct' | 'kwh' | 'rate' | 'min' | 'max'} kind
 * @param {number} value
 * @returns {string}
 */
function formatNl(kind, value) {
  switch (kind) {
    case 'pct':
      return nlFormat({ maximumFractionDigits: 2 }).format(value);
    case 'kwh':
      return nlFormat({ maximumFractionDigits: 0 }).format(value);
    case 'rate':
      return nlFormat({
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
      }).format(value);
    default: {
      const digits = Number.isInteger(value) ? 0 : 2;
      return nlFormat({
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      }).format(value);
    }
  }
}

/**
 * The model in one Dutch line, for every model; null for none. "25% tot 3.500
 * kWh · 22% tot 5.000 kWh · 14% daarboven", "14% + € 0,0057 per kWh, samen
 * min. € 34,99 en max. € 99,99 per laadpaal per jaar", "15%".
 *
 * @param {OfferPricing | null | undefined} pricing
 * @returns {string | null}
 */
export function describeOfferPricingNl(pricing) {
  const phrases = offerPricingPhrases(pricing);
  if (!phrases) return null;
  return phrases
    .map(({ key, values }) =>
      NL[key].replace(/\{(pct|kwh|rate|min|max)\}/g, (match, /** @type {string} */ name) => {
        const value = /** @type {Record<string, number | undefined>} */ (values)[name];
        return typeof value === 'number'
          ? formatNl(/** @type {'pct' | 'kwh' | 'rate' | 'min' | 'max'} */ (name), value)
          : match;
      })
    )
    .join(OFFER_PRICING_PHRASE_SEPARATOR);
}

/**
 * A staffel's ladder in Dutch, one row per band — what the admin's review card
 * draws with src/components/offers/StaffelLadder.tsx: `{ range: "3.500 – 5.000
 * kWh", pct: "22%" }`. The same rows as staffelLadderRows of
 * src/lib/offerPricingText.ts on a Dutch page (the test holds them to it);
 * no band is the visitor's here.
 *
 * @param {readonly { fromKwh: number, commissionPct: number }[]} tiers
 * @returns {{ range: string, pct: string, current: boolean }[]}
 */
export function staffelLadderNl(tiers) {
  return staffelLadderBands(tiers).map((band) => ({
    range: NL_LADDER[band.key]
      .replace('{from}', formatNl('kwh', band.from))
      .replace('{to}', band.to === null ? '' : formatNl('kwh', band.to)),
    pct: `${formatNl('pct', band.pct)}%`,
    current: false,
  }));
}

/**
 * The model named and described in Dutch, for a review line or a mail:
 * "Staffel: 25% tot 3.500 kWh · …", "Commissie: 15%" — or '—'
 * for no model (unknown, never "0%").
 *
 * @param {OfferPricing | null | undefined} pricing
 * @returns {string}
 */
export function offerPricingLineNl(pricing) {
  const text = describeOfferPricingNl(pricing);
  if (!pricing || text === null) return '—';
  return `${OFFER_PRICING_MODEL_NAMES_NL[pricing.model]}: ${text}`;
}
